import { useEffect, useRef, useState, useCallback } from 'react';
import { environment } from '../environments/environment';
import { httpGet } from '../services/httpService';
import { suppressNextError } from '../services/httpErrorBus';
import { calculateBannerConfig, calculatePollingInterval } from '../services/bannerLogicService';
import type { MaintenanceStatusResponse, BannerConfig } from '../models/maintenance';

interface AppPollingState {
  newVersionAvailable: boolean;
  maintenanceConfig: BannerConfig;
  maintenanceStatus: MaintenanceStatusResponse | null;
}

const DEFAULT_BANNER: BannerConfig = {
  show: false,
  type: 'information',
  title: '',
  dismissible: false,
  showCountdown: false,
};

const VERSION_POLL_INTERVAL = 300_000;

async function fetchVersion(): Promise<string | null> {
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`);
    if (!response.ok) return null;
    const data = (await response.json()) as { version: string };
    return data.version;
  } catch {
    return null;
  }
}

async function fetchMaintenanceStatus(): Promise<MaintenanceStatusResponse | null> {
  try {
    suppressNextError();
    return await httpGet<MaintenanceStatusResponse>(
      `${environment.apiConfig.articleplan_api.uri}/api/maintenance/status`,
    );
  } catch {
    return null;
  }
}

export function useAppPolling(): AppPollingState {
  const [state, setState] = useState<AppPollingState>({
    newVersionAvailable: false,
    maintenanceConfig: DEFAULT_BANNER,
    maintenanceStatus: null,
  });

  const initialVersionRef = useRef<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const currentIntervalMs = useRef(VERSION_POLL_INTERVAL);

  const poll = useCallback(async () => {
    const [version, maintenance] = await Promise.all([
      fetchVersion(),
      fetchMaintenanceStatus(),
    ]);

    // Version check
    let versionMismatch = false;
    if (version) {
      if (initialVersionRef.current === null) {
        initialVersionRef.current = version;
      } else if (version !== initialVersionRef.current) {
        versionMismatch = true;
      }
    }

    // Maintenance banner
    const bannerConfig = calculateBannerConfig(maintenance);
    const nextInterval = Math.min(
      VERSION_POLL_INTERVAL,
      calculatePollingInterval(maintenance),
    );

    setState({
      newVersionAvailable: versionMismatch,
      maintenanceConfig: bannerConfig,
      maintenanceStatus: maintenance,
    });

    // Adjust polling interval if maintenance urgency changed
    if (nextInterval !== currentIntervalMs.current) {
      currentIntervalMs.current = nextInterval;
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = setInterval(poll, nextInterval);
      }
    }
  }, []);

  // Auto-reload when tab becomes hidden and a new version is detected
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && state.newVersionAvailable) {
        globalThis.location.reload();
        return;
      }
      // Tab became visible — run an immediate poll
      if (document.visibilityState === 'visible') {
        poll();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [state.newVersionAvailable, poll]);

  // Start polling on mount
  useEffect(() => {
    poll();
    intervalRef.current = setInterval(poll, currentIntervalMs.current);
    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
      }
    };
  }, [poll]);

  return state;
}
